<template>
  <div v-if="allSections.length">
    <!-- options -->
    <div>
      <div class="d-flex justify-space-between">
        <BaseButton
          :text="`Back to ${
            $vuetify.breakpoint.lgAndUp ? `'Your application'` : `'Application'`
          }`"
          flat
          ripple
          class="font-weight-bold d-flex ml-n5"
          icon="mdi-chevron-left"
          color="#106175"
          small
          @click="
            $router.push({ name: ROUTE_NAME.RENT_PROTECTION.B2B.APPLICATION })
          "
        />
        <BaseButton
          :text="$vuetify.breakpoint.lgAndUp ? 'Save &amp; exit' : 'Save'"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3"
          icon="mdi-content-save"
          color="#106175"
          small
          @click="toggleGlobalSaveAndExitDialog"
        />
      </div>
      <h2
        class="font-24 font-lg-28 font-weight-bold primary--text mt-3 mt-lg-5"
      >
        Payment
      </h2>
    </div>

    <!-- the form -->
    <ValidationObserver ref="observerFormPaymentDetails" v-slot="{ validate }">
      <form @submit.prevent="onSubmit(validate)">
        <BaseLabel text="Payment method" class="mt-3 mt-lg-9 mb-2" />
        <BaseToggleButton
          v-model="paymentMethodLocal"
          :toggleButtonText="{
            yes: 'Direct Debit',
            yesDataUiid: 'PaymentMethod_DirectDebit',
            no: $vuetify.breakpoint.lgAndUp
              ? 'Credit / Debit Card'
              : 'Credit Card',
            noDataUiid: 'PaymentMethod_CreditCardDirectDebit',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '240' : ''"
        />

        <!-- payment frequency -->
        <template v-if="isDirectDebit">
          <BaseLabel text="Payment frequency" class="mt-3 mt-lg-9 mb-2" />
          <BaseSelect
            dataUiid="PaymentFrequency"
            v-model="paymentFrequency.Value"
            :items="
              setItemDataUiids('PaymentFrequency', generatePaymentFrequency)
            "
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Payment frequency',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          />
          <template v-if="paymentFrequency.Value === 'Monthly'">
            <BaseAlert color="#FFF1E4" class="pa-4 mt-3">
              <div class="d-flex flex-no-wrap">
                <div class="mr-3">
                  <v-icon color="#fb8c00">mdi-alert</v-icon>
                </div>
                <div>
                  <span class="font-weight-bold">Important!</span>&nbsp;By
                  selecting the monthly payment option you have chosen to enter
                  into a credit agreement. Failure to keep up the payments is a
                  breach of this agreement and may result in the cancellation of
                  your insurance policy. Please tick the box below to confirm
                  that the repayments are affordable and you are happy to enter
                  into a credit agreement. If you feel the payments may not be
                  affordable, please consider other payment options.
                </div>
              </div>
            </BaseAlert>

            <BaseCheckBox
              className="mt-3 font-weight-bold primary--text"
              label="I confirm that I have read, understood and agreed to the above statement."
              hideDetails="auto"
              :checked="repaymentConfirmation.Value"
              @change="(val) => (repaymentConfirmation.Value = val)"
              v-if="isMonthlyPayment"
              :rules="{
                required: {
                  required: true,
                  fieldLabel: 'Repayment confirmation',
                },
              }"
            />
          </template>
        </template>

        <LayoutApplicationSummaryCard
          class="mb-10"
          @showPremiumBreakdownMonthly="showPremiumBreakdownMonthly"
          @showPremiumBreakdownAnnual="showPremiumBreakdownAnnual"
          @shareQuote="shareQuote"
          @printQuote="printQuote"
          :productGroup="productGroup"
          :cardTitle="
            $vuetify.breakpoint.lgAndUp
              ? 'Your Rent Protection Insurance quote'
              : 'Your quote'
          "
          annualTitle="One off payment"
          :showPayByCreditDebitCardMessage="false"
          :isRetrievedQuote="$route.params.type === 'resumeQuote'"
        />

        <!-- direct debit content -->
        <template v-if="isDirectDebit">
          <BaseLabel text="Preferred Direct Debit date" class="mb-2" />
          <BaseSelect
            v-model="directDebitDate.Value"
            :vid="directDebitDate.InterfaceKey"
            dataUiid="DirectDebitDate"
            :isEnableSortOrderListItems="false"
            :items="
              setItemDataUiids(
                'DirectDebitDate',
                DIRECT_DEBIT_DATE_ITEMS_TEXT_FULL
              )
            "
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Preferred DD date',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '571px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          />

          <BaseAlert
            v-if="directDebitDate.Value"
            color="#FFF1E4"
            class="mt-4 mb-0 pa-4"
          >
            <div class="d-flex flex-no-wrap">
              <div class="mr-3">
                <v-icon color="#fb8c00">mdi-alert</v-icon>
              </div>
              <div>
                <span class="font-weight-bold">Important!</span> It can take up
                to ten working days to set up a Direct Debit which means your
                first payment may not be taken on your preferred date. The date
                of your first payment will be shown in your Welcome Letter. The
                rest of your payments will be taken on the date you choose from
                the options provided.
              </div>
            </div>
          </BaseAlert>

          <h3 class="font-20 font-weight-bold primary--text mt-10 mb-3">
            Payment details
          </h3>

          <BaseAlert
            color="#ebf5fd"
            icon="mdi-lock"
            alertText="Your payment is secure."
            alertTextClass="font-18 font-weight-bold primary--text"
            alertBorder
            alertBorderColor="#5285C0"
            alertBorderPosition="left"
            prominent
            prominentColor="#5285C0"
            prominentIconColor="white"
            prominentIconSize="19"
            rounded="0"
            :max-width="$vuetify.breakpoint.lgAndUp ? '571px' : '100%'"
          />

          <BaseLabel text="Account holder name" class="mt-10 mb-2" />
          <BaseInput
            dataUiid="AccountHolderName"
            v-model="bankAccountName.Value"
            :vid="bankAccountName.InterfaceKey"
            className="mb-9"
            hideDetails="auto"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Account holder name',
              },
              regex: {
                pattern: '^[a-zA-Z&\\s-]*$',
                customMessage:
                  'Please enter only alpha characters, ampersands, spaces and hyphens in this field.',
              },
              maxLength: {
                length: 30,
                fieldLabel: 'Account holder name',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '571px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          />

          <div class="error-message-fit-input">
            <div class="d-block d-lg-inline-block">
              <BaseLabel text="Sort code" class="mb-2" />
              <BaseInput
                dataUiid="SortCode"
                v-model="bankSortCode.Value"
                :vid="bankSortCode.InterfaceKey"
                className="mb-9"
                hideDetails="auto"
                mask="##-##-##"
                :rules="{
                  required: {
                    required: true,
                    fieldLabel: 'Sort code',
                  },
                  regex: {
                    pattern: SORT_CODE_REGEX,
                    customMessage: 'Please enter numerical values only.',
                  },
                  max: {
                    length: 8,
                    errorMessageTemplate: ERROR_MAX_LENGTH_SORT_CODE,
                    fieldLabel: 'Sort code',
                  },
                  min_length: {
                    length: 8,
                    errorMessageTemplate: ERROR_MIN_LENGTH_SORT_CODE,
                    fieldLabel: 'Sort code',
                  },
                }"
                :style="{
                  'max-width': $vuetify.breakpoint.lgAndUp ? '219px' : '100%',
                }"
                :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              />
            </div>
            <div class="d-block d-lg-inline-block ml-lg-6 acc_no__wrapper">
              <BaseLabel text="Account number" class="mb-2" />
              <BaseInput
                dataUiid="AccountNumber"
                v-model="bankAccountNo.Value"
                :vid="bankAccountNo.InterfaceKey"
                className="mb-9"
                hideDetails="auto"
                :rules="{
                  regex: {
                    pattern: '^[0-9]*$',
                    customMessage: 'Please enter numerical values only.',
                  },
                  required: {
                    required: true,
                    fieldLabel: 'Account number',
                  },
                  min: {
                    length: 8,
                    fieldLabel: 'Account number',
                  },
                  maxLength: {
                    length: 8,
                    fieldLabel: 'Account number',
                  },
                }"
                :style="{
                  'max-width': $vuetify.breakpoint.lgAndUp ? '328px' : '100%',
                  'min-width': $vuetify.breakpoint.lgAndUp ? '328px' : '',
                }"
                :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              />
            </div>
          </div>

          <h3 class="font-20 font-weight-bold primary--text mb-1 mb-lg-0">
            Stay covered
          </h3>
          <a
            class="text-decoration-underline font-weight-bold d-block d-lg-none"
            style="color: #4d7fb8"
            @click="showDirectDebitRenewalInfo"
            >Find out more</a
          >

          <BaseRadio
            :className="`mt-3 ${$vuetify.breakpoint.lgAndUp ? 'pr-6' : ''}`"
            v-model="autoRenewalConsent.Value"
            data-uiid="AutoRenewalConsent"
            pslStyle="card"
            :cardConfigs="{
              autoRenew: AUTO_RENEW_NONE_CHEAPEST_PRICE,
              doNotAutoRenew: DO_NOT_AUTO_RENEW_NONE_OFFER_RP,
            }"
          />
        </template>

        <!-- credit/debit card content -->
        <template v-else>
          <BaseAlert
            color="#ebf5fd"
            class="credit-debit-alert pa-4 pr-6 ma-0 mt-3"
          >
            <div class="d-flex flex-no-wrap">
              <div class="mr-3">
                <v-icon color="#4d7fb8">mdi-information</v-icon>
              </div>
              <div>
                <p>
                  <span class="font-weight-bold">Important!</span>&nbsp;As
                  you’ve chosen to pay by annual credit/debit card, you’ve been
                  opted out of our auto-renewal service. If you would prefer to
                  opt into our auto-renewal service, please change your payment
                  method to Direct Debit above.
                </p>
                <p class="mb-0">
                  Paymentshield will contact you with a renewal quote, which
                  includes the price and cover, 30 days before your renewal is
                  due. You will need to contact Paymentshield to renew the
                  policy or you will need to arrange cover elsewhere.
                </p>
              </div>
            </div>
          </BaseAlert>
        </template>

        <BaseButton
          depressed
          rounded
          dense
          class="font-weight-bold font-20 pa-7 px-13 mt-10 mb-3 primary--text"
          type="submit"
          icon="mdi mdi-lock"
          :loading="isApplying"
          text="SUBMIT"
          :color="'psorange'"
          :block="$vuetify.breakpoint.lgAndUp ? false : true"
        />
      </form>
    </ValidationObserver>

    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- premium breakdown dialog -->
    <BaseDialog
      ref="DialogContentPremiumBreakdown"
      windowClass="pa-0 pa-xl-4 pb-xl-0"
      headerClass="pa-4"
    >
      <template v-slot:content="{ data }">
        <DialogContentPremiumBreakdown
          :breakdownModel="data"
          :insurancePremiumTitle="'Rent protection premium (ex IPT)'"
          :showTotalPremiumIPT="isMonthlyPayment"
        ></DialogContentPremiumBreakdown>
      </template>
    </BaseDialog>

    <!-- share dialog -->
    <BaseDialog
      ref="shareDialog"
      headerIcon="mdi-share-variant"
      headerIconColor="#106175"
    >
      <template v-slot:content="{ data }">
        <DialogContentShareQuotes
          :content="getCover"
          :quoteId="data"
          @success="shareQuotesSuccess"
        ></DialogContentShareQuotes>
      </template>
    </BaseDialog>

    <!-- print dialog -->
    <BaseDialog
      ref="printQuotes"
      headerIcon="mdi-printer"
      headerIconColor="#106175"
      headerDivider
      windowClass="pa-0 pb-xl-0 mb-0"
      headerClass="px-7 pt-6 pb-3"
      contentClass="mb-0"
    >
      <template v-slot:content="{ data }">
        <DialogContentPrintQuotes :data="data"></DialogContentPrintQuotes>
      </template>
    </BaseDialog>

    <BaseDialog
      ref="directDebitRenewalInfo"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-4 pb-5 mb-n1"
      headerDivider
    >
      <template v-slot:content>
        <DialogContentAutoRenewalInfoMobile
          :cardConfigs="{
            autoRenew: AUTO_RENEW_NONE_CHEAPEST_PRICE,
            doNotAutoRenew: DO_NOT_AUTO_RENEW_NONE_OFFER_RP,
          }"
        />
      </template>
    </BaseDialog>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from "vuex";
import { ValidationObserver } from "vee-validate";
import {
  APPLICATION_MODULE,
  QUESTIONS_MODULE,
} from "@/store/types/module-types";
import { setItemDataUiids } from "@/constants/component.constants.js";
import {
  PAYMENT_FREQUENCY_ITEMS_ANNUAL_ONE_OFF,
  DIRECT_DEBIT_DATE_ITEMS_TEXT_FULL,
  AUTO_RENEW_NONE_CHEAPEST_PRICE,
  DO_NOT_AUTO_RENEW_NONE_OFFER_RP,
} from "@/constants/payment.constants.js";
import { scrollTop, getErrorMessage, convertToBool } from "@/common/utils";

// LayoutApplicationSummaryCard dependencies
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import { ANNUAL, MONTHLY } from "@/constants/common.constants";
import { ROUTE_NAME } from "@/constants/route.constants";
import { getProductGroup } from "@/common/question-set";
import { TERM_OF_INSURANCE_VALUE } from "@/constants/answer.constants";
import {
  ERROR_MAX_LENGTH_SORT_CODE,
  ERROR_MIN_LENGTH_SORT_CODE,
} from "@/constants/message.constants";
import { SORT_CODE_REGEX } from "@/constants/regex-patterns.constants";
import BaseButton from "@/components/BaseComponents/BaseButton";
import BaseLabel from "@/components/BaseComponents/BaseLabel";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton";
import BaseSelect from "@/components/BaseComponents/BaseSelect";
import BaseAlert from "@/components/BaseComponents/BaseAlert";
import BaseCheckBox from "@/components/BaseComponents/BaseCheckBox";
import BaseInput from "@/components/BaseComponents/BaseInput";
import BaseDialog from "@/components/BaseComponents/BaseDialog";
import BaseRadio from "@/components/BaseComponents/BaseRadio";

// LayoutApplicationSummaryCard components
import LayoutApplicationSummaryCard from "@/components/LayoutComponents/LayoutApplicationSummaryCard";
import DialogContentPremiumBreakdown from "@/components/QuoteResult/DialogContentPremiumBreakdown";
import DialogContentShareQuotes from "@/components/QuoteResult/DialogContentShareQuotes";
import DialogContentPrintQuotes from "@/components/QuoteResult/DialogContentPrintQuotes";
import DialogContentAutoRenewalInfoMobile from "@/views/StageApplication/DialogContentAutoRenewalInfoMobile";

export default {
  name: "RentProtectionPaymentPage",
  components: {
    ValidationObserver,
    BaseButton,
    BaseLabel,
    BaseToggleButton,
    BaseSelect,
    BaseAlert,
    BaseCheckBox,
    BaseInput,
    BaseDialog,
    BaseRadio,
    LayoutApplicationSummaryCard,
    DialogContentPremiumBreakdown,
    DialogContentShareQuotes,
    DialogContentPrintQuotes,
    DialogContentAutoRenewalInfoMobile,
  },
  mixins: [PremiumBreakdownMixin],
  data() {
    return {
      setItemDataUiids,
      PAYMENT_FREQUENCY_ITEMS_ANNUAL_ONE_OFF,
      DIRECT_DEBIT_DATE_ITEMS_TEXT_FULL,
      AUTO_RENEW_NONE_CHEAPEST_PRICE,
      DO_NOT_AUTO_RENEW_NONE_OFFER_RP,
      ANNUAL,
      MONTHLY,
      ROUTE_NAME,
      SORT_CODE_REGEX,
      ERROR_MAX_LENGTH_SORT_CODE,
      ERROR_MIN_LENGTH_SORT_CODE,
      paymentMethod: null,
      paymentMethodLocal: true,
      paymentFrequency: null,
      repaymentConfirmation: null,
      bankAccountName: null,
      bankSortCode: null,
      bankAccountNo: null,
      directDebitDate: null,
      autoRenewalConsent: null,
      quoteDocuments: [],
      documentList: [],
      overlay: false,
      isApplying: false,
    };
  },
  computed: {
    ...mapGetters(APPLICATION_MODULE, [
      "allSections",
      "selectedQuote",
      "getMonthCoverText",
    ]),
    ...mapGetters(QUESTIONS_MODULE, [
      "getCover",
      "getQuoteRequestId",
      "callChannel",
      "httpConfig",
      "insurerQuotesModel",
      "qsId",
      "branchNumber",
      "termOfInsurance",
    ]),
    ...mapState(APPLICATION_MODULE, ["questionMap"]),
    generatePaymentFrequency() {
      let frequencyItems = this.PAYMENT_FREQUENCY_ITEMS_ANNUAL_ONE_OFF;
      if (this.termOfInsurance === TERM_OF_INSURANCE_VALUE.SixMonths) {
        frequencyItems = this.PAYMENT_FREQUENCY_ITEMS_ANNUAL_ONE_OFF.filter(
          (item) => {
            return item.value === "Annual";
          }
        );
      }
      return frequencyItems;
    },
    productGroup() {
      return getProductGroup(this.qsId);
    },
    isDirectDebit() {
      return this.paymentMethodLocal;
    },
    isMonthlyPayment() {
      return this.paymentFrequency.Value === MONTHLY;
    },
    paymentFrequencyForDocs() {
      // Prevent returning an empty string for Payment Frequency if the payment method is card.
      return this.paymentMethod?.Value === "CreditCard"
        ? ANNUAL
        : this.paymentFrequency.Value;
    },
    isDirectDebitDetailsValid() {
      return !!this.bankSortCode.Value && !!this.bankAccountNo.Value;
    },
    premium() {
      const breakdown = this.buildPremiumBreakdownModel(
        this.selectedQuote,
        "DirectDebit",
        this.paymentFrequency.Value
      );
      return breakdown.premium;
    },
  },
  watch: {
    paymentMethodLocal(val) {
      if (val) {
        this.paymentMethod.Value = "DirectDebit";
      } else {
        this.paymentMethod.Value = "CreditCard";
      }
    },
  },
  created() {
    this.paymentMethod = this.questionMap["PaymentMethod"];
    if (!this.paymentMethod.Value) this.paymentMethod.Value = "DirectDebit";
    if (this.paymentMethod.Value && this.paymentMethod.Value === "CreditCard")
      this.paymentMethodLocal = false;

    this.paymentFrequency = this.questionMap["PaymentFrequency"];
    this.repaymentConfirmation = this.questionMap["RepaymentConfirmation"];
    this.bankAccountName = this.questionMap["BankAccountName"];
    this.bankSortCode = this.questionMap["BankSortCode"];
    this.bankAccountNo = this.questionMap["BankAccountNo"];
    this.directDebitDate = this.questionMap["DirectDebitDate"];
    this.autoRenewalConsent = this.questionMap["AutoRenewalConsent"];

    // when retrieving a quote convert "Boolean Strings" to Booleans
    this.repaymentConfirmation.Value = convertToBool(
      this.repaymentConfirmation.Value
    );
    this.autoRenewalConsent.Value = convertToBool(
      this.autoRenewalConsent.Value
    );
  },
  mounted() {
    scrollTop();
    // Can't go forward automatically from this page
    this.setRestoringLastStep(false);
    this.setCurrentRefObserverForm(this.$refs.observerFormPaymentDetails);
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "toggleGlobalSaveAndExitDialog",
      "setRestoringLastStep",
      "getDataToPrintQuote",
      "saveQuoteResults",
      "getDocuments",
      "setCurrentRefObserverForm",
    ]),
    ...mapActions(APPLICATION_MODULE, [
      "setCardPaymentStatus",
      "handleApplicationSubmission",
      "setSessionStorageItems",
      "proceedToCardPayment",
      "setPaymentErrorMessage",
      "clearPaymentErrorMessage",
    ]),
    async onSubmit(validate) {
      const isFormValid = await validate();
      if (!isFormValid) {
        const el = this.$el.querySelector(".field-invalid:first-of-type");
        this.$vuetify.goTo(el, { offset: 50 });
        return;
      }
      this.submitForm();
    },
    showDirectDebitRenewalInfo() {
      this.$refs.directDebitRenewalInfo.open("Stay covered");
    },
    async shareQuote(quoteId) {
      try {
        this.overlay = true;

        await this.saveQuoteResults();

        this.$refs.shareDialog.open("Share via email", quoteId);
      } catch (error) {
        console.error(error);
      } finally {
        this.overlay = false;
      }
    },
    shareQuotesSuccess() {
      this.$refs.shareDialog.cancel();
    },
    async saveQuotesAndGetDocumentsAsync() {
      await this.saveQuoteResults();

      return await this.getDocuments();
    },
    async printQuote(quote) {
      try {
        this.overlay = true;
        const docs = await this.saveQuotesAndGetDocumentsAsync();
        const paymentFrequency = this.paymentFrequencyForDocs;
        var data = await this.getDataToPrintQuote({
          docs,
          quote,
          paymentFrequency,
        });
        this.$refs.printQuotes.open("Download for printing", data);
      } catch (error) {
        console.error(error);
      } finally {
        this.overlay = false;
      }
    },
    submitForm() {
      this.setCardPaymentStatus("");
      if (this.paymentMethod.Value === "DirectDebit") {
        this.submitDirectDebitApplication();
      } else {
        this.submitCardPaymentApplication();
      }
    },
    submitDirectDebitApplication() {
      this.isApplying = true;

      this.handleApplicationSubmission()
        .then(() => {
          this.isApplying = false;
          this.setSessionStorageItems();
          this.clearPaymentErrorMessage();
          this.$router.push({
            name: ROUTE_NAME.RENT_PROTECTION.B2B.COMPLETE,
          });
        })
        .catch((error) => {
          this.isApplying = false;
          this.setPaymentErrorMessage(getErrorMessage(error));
          console.log("Apply error:", error);
        });
    },
    submitCardPaymentApplication() {
      this.isApplying = true;

      this.handleApplicationSubmission()
        .then(() => {
          this.setSessionStorageItems();
          this.clearPaymentErrorMessage();
          this.proceedToCardPayment();
        })
        .catch((error) => {
          this.isApplying = false;
          this.setPaymentErrorMessage(getErrorMessage(error));
          console.log("Apply error:", error);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.v-text-field__details) {
  margin-bottom: 0 !important;
}
.credit-debit-alert {
  letter-spacing: 0.35px;
  line-height: 19.5px;
}
@media screen and (min-width: $grid-breakpoints-lg) {
  :deep(.radioButtonCard) {
    .v-card {
      min-height: 475px !important;
    }
  }
}
</style>
